/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { Typography, Box, IconButton, TextField, Paper, Button, Switch, FormControlLabel } from "@mui/material"
import { Add, Delete, PersonAdd, Close } from "@mui/icons-material"
import { useTranslation } from "react-i18next"
import { withMyTheme } from "../../../theme/theme"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { WeddingTable, WeddingTableGuest } from "../../common/context/SharedFormTypes"

const ContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-width: 900px;
    margin: 0 auto;
`)

const HeaderStyle = withMyTheme((theme) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
`)

const TitleStyle = withMyTheme((theme) => css`
    font-size: 1.5rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
`)

const SubtitleStyle = withMyTheme((theme) => css`
    font-size: 0.9rem;
    color: ${theme.palette.text.secondary};
    margin-top: 0.25rem;
`)

const TablesGridStyle = withMyTheme(() => css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;

    ${mobileCss(`
        grid-template-columns: 1fr;
    `)}
`)

const TableCardStyle = withMyTheme((theme) => css`
    border: 1px solid ${theme.palette.divider};
    border-radius: 12px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }
`)

const TableHeaderStyle = withMyTheme(() => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`)

const TableNameInputStyle = withMyTheme((theme) => css`
    flex: 1;
    .MuiInputBase-input {
        font-weight: 600;
        font-size: 1rem;
        color: ${theme.palette.primary.main};
    }
`)

const GuestListStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 0.25rem;
`)

const GuestRowStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 0.4rem;

    .MuiInputBase-input {
        font-size: 0.9rem;
        padding: 6px 8px;
    }
`)

const GuestCountStyle = withMyTheme((theme) => css`
    font-size: 0.8rem;
    color: ${theme.palette.text.secondary};
    margin-top: 0.5rem;
    text-align: right;
`)

const PublishToggleStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    background: ${theme.palette.action.hover};
`)

const AddTableButtonStyle = withMyTheme((theme) => css`
    border: 2px dashed ${theme.palette.divider};
    border-radius: 12px;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    color: ${theme.palette.text.secondary};
    min-height: 120px;

    &:hover {
        border-color: ${theme.palette.primary.main};
        color: ${theme.palette.primary.main};
        background: rgba(111, 143, 118, 0.04);
    }
`)

export const TablesEditorStep = () => {
    const { t } = useTranslation()
    const { formData, updateTables } = useQuestionnaireForm()

    const published = formData.tables?.published ?? false
    const tables = formData.tables?.tables ?? []

    const handleTogglePublish = () => {
        updateTables({ published: !published })
    }

    const handleAddTable = () => {
        const newTable: WeddingTable = {
            id: `table-${Date.now()}`,
            name: '',
            guests: []
        }
        updateTables({ tables: [...tables, newTable] })
    }

    const handleRemoveTable = (tableId: string) => {
        updateTables({ tables: tables.filter(t => t.id !== tableId) })
    }

    const handleUpdateTableName = (tableId: string, name: string) => {
        updateTables({
            tables: tables.map(t => t.id === tableId ? { ...t, name } : t)
        })
    }

    const handleAddGuest = (tableId: string) => {
        const newGuest: WeddingTableGuest = {
            id: `guest-${Date.now()}`,
            name: ''
        }
        updateTables({
            tables: tables.map(t =>
                t.id === tableId ? { ...t, guests: [...t.guests, newGuest] } : t
            )
        })
    }

    const handleRemoveGuest = (tableId: string, guestId: string) => {
        updateTables({
            tables: tables.map(t =>
                t.id === tableId
                    ? { ...t, guests: t.guests.filter(g => g.id !== guestId) }
                    : t
            )
        })
    }

    const handleUpdateGuestName = (tableId: string, guestId: string, name: string) => {
        updateTables({
            tables: tables.map(t =>
                t.id === tableId
                    ? { ...t, guests: t.guests.map(g => g.id === guestId ? { ...g, name } : g) }
                    : t
            )
        })
    }

    return (
        <Box css={ContainerStyle}>
            <div css={HeaderStyle}>
                <div>
                    <Typography css={TitleStyle}>{t('tablesEditor.title')}</Typography>
                    <Typography css={SubtitleStyle}>{t('tablesEditor.subtitle')}</Typography>
                </div>
            </div>

            <div css={PublishToggleStyle}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={published}
                            onChange={handleTogglePublish}
                            color="primary"
                        />
                    }
                    label={t('tablesEditor.publishToggle')}
                />
            </div>

            <div css={TablesGridStyle}>
                {tables.map((table) => (
                    <Paper key={table.id} css={TableCardStyle} elevation={0}>
                        <div css={TableHeaderStyle}>
                            <TextField
                                css={TableNameInputStyle}
                                variant="standard"
                                placeholder={t('tablesEditor.tableName')}
                                value={table.name}
                                onChange={(e) => handleUpdateTableName(table.id, e.target.value)}
                            />
                            <IconButton size="small" onClick={() => handleRemoveTable(table.id)} color="error">
                                <Delete fontSize="small" />
                            </IconButton>
                        </div>

                        <div css={GuestListStyle}>
                            {table.guests.map((guest) => (
                                <div key={guest.id} css={GuestRowStyle}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        placeholder={t('tablesEditor.guestName')}
                                        value={guest.name}
                                        onChange={(e) => handleUpdateGuestName(table.id, guest.id, e.target.value)}
                                    />
                                    <IconButton size="small" onClick={() => handleRemoveGuest(table.id, guest.id)}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>

                        <Button
                            variant="text"
                            color="primary"
                            startIcon={<PersonAdd />}
                            onClick={() => handleAddGuest(table.id)}
                            size="small"
                        >
                            {t('tablesEditor.addGuest')}
                        </Button>

                        <div css={GuestCountStyle}>
                            {table.guests.length} {t('tablesEditor.guests')}
                        </div>
                    </Paper>
                ))}

                <div css={AddTableButtonStyle} onClick={handleAddTable}>
                    <Add fontSize="large" />
                    <Typography>{t('tablesEditor.addTable')}</Typography>
                </div>
            </div>
        </Box>
    )
}
